import { useNavigate } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";

import { Button, Grid2, IconButton, Stack, Typography } from "@mui/material";
import ContainerLoadingLogin from "../components/ContainerLoadingLogin";
import { useUserStore } from "../stores/user";
import { useState } from "react";
import {
  createStoreOnPlan,
  extendFreePeriod,
  getStorePlans,
} from "../services/store";
import { formatPrice } from "../helpers/format";
import { useFormik } from "formik";
import { IconArrowLeft } from "@tabler/icons-react";

const FreePeriodExpired = () => {
  const navigate = useNavigate();
  const { user } = useUserStore();
  const [step, setStep] = useState(0);

  const onLogout = () => {
    navigate("/");
  };

  const { data: plans }: any = useQuery({
    queryFn: async () => await getStorePlans(),
    queryKey: ["getStorePlans"],
    enabled: true,
    initialData: [],
  });

  const { mutate, isPending } = useMutation({
    mutationFn: createStoreOnPlan,
    onSuccess: () => {
      toast.success("Seu plano foi atualizado");
      navigate("/dashboard");
    },

    onError: (e: { response: { data: { message: string } } }) => {
      toast.error(
        String(e?.response?.data?.message) || "Erro ao tentar atualizar plano"
      );
    },
  });

  const {
    mutate: mutateExtendFreePeriod,
    isPending: isPendingExtendFreePeriod,
  } = useMutation({
    mutationFn: extendFreePeriod,
    onSuccess: () => {
      toast.success("Período grátis renovado");
      navigate("/dashboard");
    },

    onError: (e: { response: { data: { message: string } } }) => {
      toast.error(
        String(e?.response?.data?.message) || "Erro ao renovar período"
      );
    },
  });

  const { handleChange, values, setFieldValue, handleSubmit } = useFormik({
    initialValues: {
      id: "",
      planId: "",
    },
    onSubmit: (values) => {
      if (!values.planId) {
        toast.error("Selecione um plano");
        return;
      }
      mutate({ ...values, id: Number(user?.storeId) });
    },
  });

  return (
    <ContainerLoadingLogin loading={isPending || isPendingExtendFreePeriod}>
      <Grid2 container sx={styles.container}>
        {step === 0 && (
          <Grid2 container sx={styles.paper} spacing={4}>
            <Grid2 size={12}>
              <Stack
                sx={{ flexDirection: "row", gap: 2, alignItems: "center" }}
              >
                <IconButton onClick={() => onLogout()}>
                  <IconArrowLeft style={styles.iconStyle} />
                </IconButton>
              </Stack>
            </Grid2>
            <Grid2 size={12}>
              <Typography variant="h4" fontWeight={700}>
                Período de teste expirado
              </Typography>
            </Grid2>
            <Grid2 size={12}>
              <Typography variant="subtitle1" fontSize={16} lineHeight={2}>
                Seu período de teste grátis expirou. Não perca tempo e ative
                logo seu plano para aproveitar todos o benefícios do Fight GO.
              </Typography>
            </Grid2>
            <Grid2 size={12}>
              <Button
                fullWidth
                variant="contained"
                color="primary"
                size="large"
                onClick={() => setStep(1)}
              >
                Ativar meu plano agora
              </Button>
              {user?.store?.canRenewFreeTime && (
                <Button
                  variant="text"
                  fullWidth
                  size="large"
                  onClick={() => mutateExtendFreePeriod()}
                  sx={{ mt: 2, fontSize: 14 }}
                >
                  Renovar por +15 dias
                </Button>
              )}
            </Grid2>
          </Grid2>
        )}

        {step === 1 && (
          <Grid2 container sx={styles.paper} spacing={2}>
            <Grid2 size={12}>
              <Stack
                sx={{ flexDirection: "row", gap: 2, alignItems: "center" }}
              >
                <IconButton onClick={() => setStep(step - 1)}>
                  <IconArrowLeft style={styles.iconStyle} />
                </IconButton>
                <Typography variant="subtitle1" fontWeight={600}>
                  Cadastrar plano
                </Typography>
              </Stack>
            </Grid2>
            {plans?.map((item: any) => (
              <Grid2 size={12} key={item?.id}>
                <Stack
                  minWidth={300}
                  key={item?.id}
                  spacing={1}
                  sx={{
                    padding: 2,
                    bgcolor:
                      Number(values?.planId) === Number(item?.id)
                        ? "primary.main"
                        : "secondary.light",
                    borderRadius: 2,
                    "&&:hover": {
                      cursor: "pointer",
                      bgcolor: "primary.main",
                    },
                  }}
                  onClick={() => setFieldValue("planId", Number(item?.id))}
                  onChange={handleChange}
                >
                  <Stack
                    sx={{
                      flexDirection: "row",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography fontWeight={700}>{item?.name}</Typography>
                    {Number(item?.value) > 0 && (
                      <Typography fontWeight={700}>
                        {formatPrice(item?.value)}
                      </Typography>
                    )}
                    {Number(item?.value) === 0 && (
                      <Typography fontWeight={700} color="green">
                        Grátis
                      </Typography>
                    )}
                  </Stack>
                  <Typography>{item?.description}</Typography>
                </Stack>
              </Grid2>
            ))}
            <Grid2 size={12}>
              <Button
                fullWidth
                variant="contained"
                color="primary"
                size="large"
                onClick={() => handleSubmit()}
              >
                Cadastrar plano
              </Button>
            </Grid2>
          </Grid2>
        )}
      </Grid2>
    </ContainerLoadingLogin>
  );
};

const styles = {
  paper: {
    width: "500px",
    backgroundColor: "background.default",
    padding: "2rem",
    borderRadius: "24px",
  },
  avatar: {
    backgroundColor: "blueviolet",
  },
  iconStyle: {
    color: "primary.main",
  },
  container: {
    justifyContent: "center",
    alignItems: "center",
    height: "100vh",
  },
};

export default FreePeriodExpired;
